import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { listingsApi, userApi, plansApi, chatApi, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { 
  User, Package, CreditCard, Plus, Edit, Trash2, Loader2, 
  Check, Crown, AlertTriangle, MessageCircle, ImageOff, Star,
  TrendingUp, Eye, Sparkles, Infinity, Flame, Zap, CheckCircle2, RotateCcw
} from 'lucide-react';

// Icon mapping for plan features
const featureIcons = {
  Package: Package,
  MessageCircle: MessageCircle,
  Eye: Eye,
  Infinity: Infinity,
  Star: Star,
  TrendingUp: TrendingUp,
  Sparkles: Sparkles
};

export default function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'profile');
  const [listings, setListings] = useState([]);
  const [plans, setPlans] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [profileData, setProfileData] = useState({
    name: user?.name || ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [listingsRes, plansRes, statsRes, conversationsRes] = await Promise.all([
          listingsApi.getMy(),
          plansApi.getAll(),
          userApi.getStats(),
          chatApi.getConversations()
        ]);
        setListings(listingsRes.data);
        setPlans(plansRes.data);
        setUserStats(statsRes.data);
        setConversations(conversationsRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (user) {
      setProfileData({ name: user.name || '' });
    }
  }, [user]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await userApi.updateProfile(profileData);
      await refreshUser();
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteListing = async (id) => {
    setDeletingId(id);
    try {
      await listingsApi.delete(id);
      setListings(prev => prev.filter(l => l.id !== id));
      const { data } = await userApi.getStats();
      setUserStats(data);
      toast.success('Listing deleted');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleToggleSold = async (id) => {
    try {
      const { data } = await listingsApi.toggleSold(id);
      setListings(prev => prev.map(l => l.id === id ? { ...l, status: data.status } : l));
      toast.success(data.status === 'sold' ? 'Listing marked as sold' : 'Listing reactivated');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const handlePlanSelect = async (planId) => {
    if (planId === user?.plan) return;
    
    if (planId === 'free') {
      // Downgrade to free - just update
      try {
        await userApi.updatePlan(planId);
        await refreshUser();
        const { data } = await userApi.getStats();
        setUserStats(data);
        toast.success('Plan updated to Free');
      } catch (error) {
        toast.error(formatApiError(error));
      }
    } else {
      // For paid plans, show message (Stripe integration later)
      toast.info('Payment integration coming soon! For now, plan upgraded for testing.');
      try {
        await userApi.updatePlan(planId);
        await refreshUser();
        const { data } = await userApi.getStats();
        setUserStats(data);
      } catch (error) {
        toast.error(formatApiError(error));
      }
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatMessageTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
  };

  const currentPlan = user?.plan || 'free';
  const listingCount = userStats?.listing_count || 0;
  const listingLimit = userStats?.listing_limit === -1 ? 'Unlimited' : userStats?.listing_limit || 5;

  return (
    <div className="min-h-screen py-8" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 
              className="text-3xl font-bold"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Dashboard
            </h1>
            <p className="text-muted-foreground">
              Manage your profile, listings, messages, and subscription
            </p>
          </div>
          <Button 
            onClick={() => navigate('/create-listing')}
            className="gap-2"
            data-testid="new-listing-btn"
          >
            <Plus className="h-4 w-4" />
            New Listing
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-8 flex-wrap h-auto gap-1 overflow-x-auto">
            <TabsTrigger value="profile" className="gap-2" data-testid="tab-profile">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">Profile</span>
            </TabsTrigger>
            <TabsTrigger value="listings" className="gap-2" data-testid="tab-listings">
              <Package className="h-4 w-4" />
              <span className="hidden sm:inline">My </span>Listings
            </TabsTrigger>
            <TabsTrigger value="messages" className="gap-2" data-testid="tab-messages">
              <MessageCircle className="h-4 w-4" />
              <span className="hidden sm:inline">Messages</span>
              {conversations.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                  {conversations.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="subscription" className="gap-2" data-testid="tab-subscription">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Subscription</span>
              <span className="sm:hidden">Plans</span>
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" data-testid="profile-content">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Profile Info */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>Profile Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleProfileUpdate} className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={profileData.name}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        data-testid="profile-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        value={user?.email || ''}
                        disabled
                        className="bg-muted"
                        data-testid="profile-email-input"
                      />
                      <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                    </div>
                    <Button type="submit" disabled={saving} data-testid="save-profile-btn">
                      {saving ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card>
                <CardHeader>
                  <CardTitle>Quick Stats</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-muted rounded-xl">
                    <p className="text-sm text-muted-foreground">Current Plan</p>
                    <p className="text-lg font-semibold capitalize" data-testid="current-plan">
                      {currentPlan}
                    </p>
                  </div>
                  <div className="p-4 bg-muted rounded-xl">
                    <p className="text-sm text-muted-foreground">Listings</p>
                    <p className="text-lg font-semibold" data-testid="listing-stats">
                      {listingCount} / {listingLimit}
                    </p>
                  </div>
                  <div className="p-4 bg-muted rounded-xl">
                    <p className="text-sm text-muted-foreground">Messages</p>
                    <p className="text-lg font-semibold" data-testid="message-stats">
                      {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings" data-testid="listings-content">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>My Listings ({listings.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : listings.length > 0 ? (
                  <div className="space-y-4">
                    {listings.map((listing) => {
                      const firstImage = listing.images?.[0];
                      const imgSrc = firstImage
                        ? firstImage.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL || ''}${firstImage}` : firstImage
                        : null;

                      return (
                      <div 
                        key={listing.id}
                        className={`flex items-center gap-4 p-4 border rounded-xl transition-colors ${listing.status === 'sold' ? 'border-red-200 bg-red-50/30' : 'border-border hover:bg-muted/50'}`}
                        data-testid={`my-listing-${listing.id}`}
                      >
                        {/* Image */}
                        <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex-shrink-0 relative">
                          {imgSrc ? (
                            <img 
                              src={imgSrc} 
                              alt={listing.title}
                              className={`w-full h-full object-cover ${listing.status === 'sold' ? 'opacity-50 grayscale' : ''}`}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                              <Package className="h-6 w-6" />
                            </div>
                          )}
                          {listing.status === 'sold' && (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">SOLD</span>
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium truncate">{listing.title}</h3>
                            {listing.status === 'sold' && (
                              <Badge variant="destructive" className="text-[10px] px-1.5 py-0" data-testid={`sold-tag-${listing.id}`}>Sold</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(listing.created_at)} · {listing.views || 0} views
                          </p>
                        </div>

                        {/* Price */}
                        {listing.price && (
                          <span className="font-medium text-primary">
                            ${listing.price}
                          </span>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {confirmDeleteId === listing.id ? (
                            <>
                              <span className="text-sm text-destructive font-medium mr-1 hidden sm:inline">Delete?</span>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteListing(listing.id)}
                                disabled={deletingId === listing.id}
                                data-testid={`confirm-delete-${listing.id}`}
                              >
                                {deletingId === listing.id ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  'Yes'
                                )}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setConfirmDeleteId(null)}
                                data-testid={`cancel-delete-${listing.id}`}
                              >
                                No
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant={listing.status === 'sold' ? 'outline' : 'ghost'}
                                size="sm"
                                onClick={() => handleToggleSold(listing.id)}
                                className={listing.status === 'sold' ? 'text-primary border-primary/30 gap-1' : 'text-orange-600 hover:text-orange-700 gap-1'}
                                data-testid={`sold-toggle-${listing.id}`}
                              >
                                {listing.status === 'sold' ? (
                                  <><RotateCcw className="h-3.5 w-3.5" /> Reactivate</>
                                ) : (
                                  <><CheckCircle2 className="h-3.5 w-3.5" /> Sold</>
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate(`/edit-listing/${listing.id}`)}
                                data-testid={`edit-listing-${listing.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setConfirmDeleteId(listing.id)}
                                className="text-destructive hover:text-destructive"
                                data-testid={`delete-listing-${listing.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No listings yet</p>
                    <Button onClick={() => navigate('/create-listing')} data-testid="create-first-listing-btn">
                      Create Your First Listing
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" data-testid="messages-content">
            <Card>
              <CardHeader>
                <CardTitle>Messages ({conversations.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                ) : conversations.length > 0 ? (
                  <div className="space-y-2">
                    {conversations.map((conv) => (
                      <Link
                        key={conv.id}
                        to={`/messages/${conv.id}`}
                        className="flex items-center gap-4 p-4 border border-border rounded-xl hover:bg-muted/50 transition-colors"
                        data-testid={`conversation-${conv.id}`}
                      >
                        {/* Listing Image */}
                        <div className="w-14 h-14 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                          {conv.listing_image ? (
                            <img 
                              src={conv.listing_image} 
                              alt={conv.listing_title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ImageOff className="h-5 w-5 text-muted-foreground/40" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="font-medium truncate">
                              {conv.other_party_name || 'Unknown'}
                            </p>
                            {conv.last_message_at && (
                              <span className="text-xs text-muted-foreground flex-shrink-0">
                                {formatMessageTime(conv.last_message_at)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground truncate">
                            {conv.listing_title}
                          </p>
                          {conv.last_message && (
                            <p className="text-sm text-muted-foreground truncate mt-1">
                              {conv.last_message}
                            </p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <MessageCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-2">No messages yet</p>
                    <p className="text-sm text-muted-foreground">
                      When you message a seller or receive messages, they'll appear here
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription" data-testid="subscription-content">
            {/* Promotion Banner */}
            <div className="mb-8 p-4 bg-primary/5 rounded-xl border border-primary/20">
              <div className="flex items-center justify-center gap-2 text-center">
                <Flame className="h-5 w-5 text-[#E53935]" />
                <span className="font-semibold text-gray-900">Limited Time Offer:</span>
                <span className="text-gray-500">Get Pro Plan FREE until August!</span>
                <Flame className="h-5 w-5 text-[#E53935]" />
              </div>
            </div>

            {/* Limit Warning */}
            {userStats && !userStats.can_create_more && currentPlan === 'free' && (
              <Card className="mb-6 border-[#E53935]/30 bg-[#E53935]/5">
                <CardContent className="p-4 flex items-start gap-3">
                  <Zap className="h-5 w-5 text-[#E53935] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">You've hit your free limit!</p>
                    <p className="text-sm text-gray-500">
                      Upgrade to Pro (free until August) to unlock unlimited listings.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Plans Grid - Improved UI */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {plans.map((plan) => {
                const isPro = plan.id === 'pro';
                const isCurrentPlan = currentPlan === plan.id;
                
                return (
                  <Card 
                    key={plan.id}
                    className={`relative overflow-hidden transition-all duration-300 ${
                      isPro 
                        ? 'border-2 border-primary shadow-xl bg-gradient-to-b from-primary/5 to-background' 
                        : 'border border-border hover:border-muted-foreground/30'
                    } ${isCurrentPlan ? 'ring-2 ring-primary ring-offset-2' : ''}`}
                    data-testid={`plan-card-${plan.id}`}
                  >
                    {/* Promotion Badge for Pro */}
                    {isPro && plan.promotion?.active && (
                      <div className="absolute top-0 right-0">
                        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl flex items-center gap-1">
                          <Flame className="h-3 w-3" />
                          {plan.promotion.badge}
                        </div>
                      </div>
                    )}

                    {/* Best Value Badge for Pro */}
                    {isPro && (
                      <div className="absolute -top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                        <Badge className="bg-primary text-primary-foreground px-4 py-1 text-sm shadow-lg">
                          <Star className="h-3 w-3 mr-1 fill-current" />
                          Best Value
                        </Badge>
                      </div>
                    )}

                    <CardContent className={`p-6 ${isPro ? 'pt-10' : 'pt-6'}`}>
                      {/* Plan Header */}
                      <div className="text-center mb-6">
                        <div className={`w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center ${
                          isPro ? 'bg-primary/10' : 'bg-muted'
                        }`}>
                          {isPro ? (
                            <Crown className={`h-7 w-7 ${isPro ? 'text-primary' : 'text-muted-foreground'}`} />
                          ) : (
                            <User className="h-7 w-7 text-muted-foreground" />
                          )}
                        </div>
                        
                        <h3 
                          className="text-2xl font-bold mb-2"
                          style={{ fontFamily: 'Outfit, sans-serif' }}
                        >
                          {plan.name}
                        </h3>
                        
                        {/* Price Display */}
                        <div className="flex items-baseline justify-center gap-2">
                          {plan.original_price && plan.price === 0 ? (
                            <>
                              <span className="text-2xl text-muted-foreground line-through">
                                ${plan.original_price}
                              </span>
                              <span className="text-4xl font-bold text-primary">FREE</span>
                            </>
                          ) : (
                            <span className="text-4xl font-bold">
                              {plan.price === 0 ? 'FREE' : `$${plan.price}`}
                            </span>
                          )}
                          {plan.price > 0 && (
                            <span className="text-muted-foreground">/month</span>
                          )}
                        </div>
                        
                        {/* Promotion Label */}
                        {isPro && plan.promotion?.active && (
                          <p className="text-sm text-orange-600 font-medium mt-2">
                            {plan.promotion.label}
                          </p>
                        )}
                        
                        <p className="text-sm text-muted-foreground mt-2">
                          {plan.listings === -1 ? 'Unlimited listings' : `Up to ${plan.listings} listings`}
                        </p>
                      </div>

                      {/* Features List */}
                      <ul className="space-y-3 mb-6">
                        {plan.features.map((feature, idx) => {
                          const featureText = typeof feature === 'string' ? feature : feature.text;
                          const iconName = typeof feature === 'object' ? feature.icon : null;
                          const FeatureIcon = iconName && featureIcons[iconName] ? featureIcons[iconName] : Check;
                          
                          return (
                            <li key={idx} className="flex items-center gap-3">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isPro ? 'bg-primary/10' : 'bg-muted'
                              }`}>
                                <FeatureIcon className={`h-3.5 w-3.5 ${isPro ? 'text-primary' : 'text-muted-foreground'}`} />
                              </div>
                              <span className="text-sm">{featureText}</span>
                            </li>
                          );
                        })}
                      </ul>

                      {/* Action Button */}
                      <Button
                        className={`w-full h-12 text-base font-semibold ${
                          isPro && !isCurrentPlan
                            ? 'bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg' 
                            : ''
                        }`}
                        variant={isCurrentPlan ? 'outline' : 'default'}
                        onClick={() => handlePlanSelect(plan.id)}
                        disabled={isCurrentPlan}
                        data-testid={`select-plan-${plan.id}`}
                      >
                        {isCurrentPlan ? (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            Current Plan
                          </>
                        ) : isPro ? (
                          <>
                            <Zap className="h-4 w-4 mr-2" />
                            Upgrade to Pro — Free!
                          </>
                        ) : (
                          'Switch to Free'
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Additional Info */}
            <p className="text-center text-sm text-muted-foreground mt-8 max-w-xl mx-auto">
              Pro plan will be $12/month after August. Lock in your free upgrade now and keep all Pro features!
            </p>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
